import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";

const COPY = {
  coaching: { title: "Book a Coaching Call", desc: "Share your details and we'll reach out to plan your personalized AI roadmap.", showMsg: true, showCompany: false },
  corporate: { title: "Enquire About Training", desc: "Tell us about your team and training needs for practical AI skills.", showMsg: true, showCompany: true },
  contact: { title: "Get in Touch", desc: "Send us a message and we'll get back to you.", showMsg: true, showCompany: false },
  free_kit: { title: "Get the Free AI Starter Kit", desc: "A practical collection of beginner-friendly AI tools, prompts and workflows.", showMsg: false, showCompany: false },
};

export const LeadDialog = ({ type, trigger }) => {
  const cfg = COPY[type] || COPY.contact;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ type, name: form.name, email: form.email, company: form.company, message: form.message });
      toast.success("Thank you! We've received your details.");
      setForm({ name: "", email: "", company: "", message: "" });
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-ink-surface border-white/10 text-white sm:max-w-md" data-testid={`lead-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{cfg.title}</DialogTitle>
          <DialogDescription className="text-slate-400">{cfg.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${type}-name`} className="text-slate-300">Name</Label>
            <Input id={`${type}-name`} data-testid={`lead-${type}-name`} value={form.name} onChange={set("name")} placeholder="Your name" className="bg-ink border-white/10 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${type}-email`} className="text-slate-300">Email</Label>
            <Input id={`${type}-email`} data-testid={`lead-${type}-email`} type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-ink border-white/10 text-white" />
          </div>
          {cfg.showCompany && (
            <div className="space-y-1.5">
              <Label htmlFor={`${type}-company`} className="text-slate-300">Company / Organization</Label>
              <Input id={`${type}-company`} data-testid={`lead-${type}-company`} value={form.company} onChange={set("company")} placeholder="Company name" className="bg-ink border-white/10 text-white" />
            </div>
          )}
          {cfg.showMsg && (
            <div className="space-y-1.5">
              <Label htmlFor={`${type}-message`} className="text-slate-300">Message</Label>
              <Textarea id={`${type}-message`} data-testid={`lead-${type}-message`} value={form.message} onChange={set("message")} placeholder="How can we help?" className="bg-ink border-white/10 text-white min-h-[90px]" />
            </div>
          )}
          <button type="submit" data-testid={`lead-${type}-submit`} disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand hover:bg-brand-light text-white font-semibold py-3.5 transition-colors disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {cfg.title}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
